/**
 * @vuelto-surface
 * mobile: ok
 * desktop: ok
 * strategy: unified
 *
 * Toolbar de la lista: chips de categoría (estructura visible del catálogo,
 * modelo categoría-primero como Mangomint) + control de orden siempre visible
 * (antes enterrado dentro del sheet de Filtros).
 */
import React, { useMemo, useState } from 'react';
import { ScrollView, StyleSheet, Text, TouchableOpacity, View } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { MasterFilterSheetModal } from '../../shared/filters/MasterFilterSheetModal';
import { operationalFilterStyles } from '../../shared/filters/operationalFilters.styles';
import { ticketColors, ticketFonts } from '../../shared/theme/ticketColors';
import { spacing } from '../../shared/theme/spacing';
import { DEFAULT_CATALOG_FILTERS, sortOptions } from '../domain/catalogFilterOptions';
import type { CatalogSort } from '../domain/catalogTypes';
import type { CategoryFilterOption } from '../domain/categoryOptions';

const SORT_SHEET_OPTIONS = sortOptions.map((o) => ({ id: o.id, name: o.label }));

type CatalogListToolbarProps = {
  /** Opciones incluyendo la entrada «Todas» (`id: 'all'`). */
  categories: CategoryFilterOption[];
  selectedCategoryId: string | null | 'all';
  onSelectCategory: (id: string | 'all') => void;
  sortBy: CatalogSort;
  onSelectSort: (sort: CatalogSort) => void;
};

export function CatalogListToolbar({
  categories,
  selectedCategoryId,
  onSelectCategory,
  sortBy,
  onSelectSort,
}: CatalogListToolbarProps) {
  const [sortSheetOpen, setSortSheetOpen] = useState(false);

  const currentSortLabel = useMemo(
    () => sortOptions.find((o) => o.id === sortBy)?.label ?? 'Nombre A→Z',
    [sortBy]
  );
  const sortIsDefault = sortBy === DEFAULT_CATALOG_FILTERS.sortBy;
  // categoryFilters siempre incluye «Todas» — chips solo si hay categorías reales.
  const showChips = categories.length > 1;

  return (
    <View style={styles.toolbar} testID="catalog.list.toolbar">
      {showChips ? (
        <ScrollView
          horizontal
          showsHorizontalScrollIndicator={false}
          style={styles.chipsScroll}
          contentContainerStyle={styles.chipsContent}
        >
          {categories.map((category) => {
            const selected =
              category.id === 'all'
                ? selectedCategoryId === 'all' || selectedCategoryId == null
                : selectedCategoryId === category.id;
            return (
              <TouchableOpacity
                key={category.id}
                style={[
                  operationalFilterStyles.chip,
                  operationalFilterStyles.chipTicket,
                  selected && operationalFilterStyles.chipActiveTicket,
                ]}
                onPress={() => onSelectCategory(category.id)}
                accessibilityRole="button"
                accessibilityState={{ selected }}
                accessibilityLabel={`Filtrar por ${category.label}`}
                testID={`catalog.category.chip.${category.id}`}
                activeOpacity={0.7}
              >
                <Text
                  style={[
                    operationalFilterStyles.chipText,
                    operationalFilterStyles.chipTextTicket,
                    selected && operationalFilterStyles.chipTextActiveTicket,
                  ]}
                  numberOfLines={1}
                >
                  {category.label}
                </Text>
              </TouchableOpacity>
            );
          })}
        </ScrollView>
      ) : (
        <View style={styles.chipsSpacer} />
      )}
      <TouchableOpacity
        style={[
          operationalFilterStyles.chip,
          operationalFilterStyles.chipTicket,
          styles.sortTrigger,
          !sortIsDefault && operationalFilterStyles.chipActiveTicket,
        ]}
        onPress={() => setSortSheetOpen(true)}
        accessibilityRole="button"
        accessibilityLabel={`Ordenar por: ${currentSortLabel}`}
        testID="catalog.sort.open"
        activeOpacity={0.7}
      >
        <Ionicons
          name="swap-vertical-outline"
          size={14}
          color={sortIsDefault ? ticketColors.ink2 : ticketColors.brandInk}
        />
        <Text
          style={[
            operationalFilterStyles.chipText,
            operationalFilterStyles.chipTextTicket,
            !sortIsDefault && operationalFilterStyles.chipTextActiveTicket,
            styles.sortTriggerText,
          ]}
          numberOfLines={1}
        >
          {currentSortLabel}
        </Text>
      </TouchableOpacity>
      <MasterFilterSheetModal
        visible={sortSheetOpen}
        title="Ordenar por"
        options={SORT_SHEET_OPTIONS}
        selectedId={sortBy}
        showAllOption={false}
        appearance="ticket"
        onSelect={(id) => {
          if (id) onSelectSort(id as CatalogSort);
        }}
        onClose={() => setSortSheetOpen(false)}
      />
    </View>
  );
}

const styles = StyleSheet.create({
  toolbar: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.sm,
    marginBottom: spacing.sm,
  },
  chipsScroll: {
    // flex:1 solo — un flexGrow:0 extra colapsa el ScrollView a 0px en web.
    flex: 1,
    minWidth: 0,
  },
  chipsContent: {
    gap: spacing.xs,
    alignItems: 'center',
  },
  chipsSpacer: {
    flex: 1,
  },
  sortTrigger: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
    flexShrink: 1,
  },
  sortTriggerText: {
    fontFamily: ticketFonts.sans,
    flexShrink: 1,
  },
});
